import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';
import {
  AlertTriangle,
  CheckCircle2,
  Copy,
  KeyRound,
  Loader2,
  Network,
  RefreshCw,
  ShieldAlert,
  ShieldCheck,
  TestTube2,
  Trash2,
} from 'lucide-react';
import { cn } from '@/lib/utils';

type BridgeStatus = 'active' | 'inactive' | 'error' | 'retired';
type PreparedMode = 'off' | 'monitor' | 'enforce';
type DatabaseType = 'MSSQL' | 'FIREBIRD';
type DatasetType = 'CARGOERP' | 'EZSHIP' | 'PRO2' | 'CUSTOM';

interface BridgeSummary {
  id: string;
  tenantId: string;
  bridgeCode: string;
  bridgeType: string;
  databaseType: DatabaseType;
  datasetType: DatasetType;
  label: string;
  status: BridgeStatus;
  pathSlug: string;
  baseUrl: string;
  bridgeUrl: string;
  healthcheckUrl: string | null;
  sourceKey: string | null;
  readOnlyMode: boolean;
  preparedMode: PreparedMode;
  allowedHandlers: string[];
  allowedIps: string[];
  metadata: Record<string, unknown>;
  lastTestedAt: string | null;
  lastTestOk: boolean | null;
  lastError: string | null;
  createdAt: string;
  updatedAt: string;
}

interface BridgeKey {
  id: string;
  bridgeId: string;
  keySlot: string;
  keyFingerprint: string;
  status: string;
  validFrom: string | null;
  validTo: string | null;
  lastUsedAt: string | null;
  lastUsedIp: string | null;
}

interface BridgeDetail extends BridgeSummary {
  keys: BridgeKey[];
}

interface BridgeAccessLog {
  id: string;
  request_id: string | null;
  event_type: string;
  handler: string | null;
  action: string | null;
  source_ip: string | null;
  key_fingerprint: string | null;
  prepared_status: string;
  decision: string;
  status_code: number | null;
  reason: string | null;
  created_at: string;
}

interface BridgeAlert {
  id: string;
  severity: string;
  alert_code: string;
  title: string;
  detail: string;
  status: string;
  created_at: string;
}

interface BridgeConfigExport {
  bridgeId: string;
  tenantId: string;
  bridgeCode: string;
  pathSlug: string;
  bridgeUrl: string;
  healthcheckUrl: string | null;
  preparedMode: PreparedMode;
  platformApiBaseUrl: string;
  platformValidateUrl: string;
  platformCompleteUrl: string;
  passkeys: string[];
}

type Paginated<T> = {
  items: T[];
  total: number;
  page: number;
  limit: number;
  hasMore: boolean;
};

type BridgeForm = {
  bridgeCode: string;
  label: string;
  bridgeType: string;
  databaseType: DatabaseType;
  datasetType: DatasetType;
  status: BridgeStatus;
  baseUrl: string;
  pathSlug: string;
  healthcheckUrl: string;
  sourceKey: string;
  preparedMode: PreparedMode;
  readOnlyMode: 'true' | 'false';
  allowedHandlersCsv: string;
  allowedIpsCsv: string;
};

const EMPTY_FORM: BridgeForm = {
  bridgeCode: '',
  label: '',
  bridgeType: 'ashx',
  databaseType: 'MSSQL',
  datasetType: 'CUSTOM',
  status: 'active',
  baseUrl: '',
  pathSlug: '',
  healthcheckUrl: '',
  sourceKey: '',
  preparedMode: 'monitor',
  readOnlyMode: 'true',
  allowedHandlersCsv: '',
  allowedIpsCsv: '',
};

const STATUS_BADGE: Record<string, string> = {
  active: 'bg-emerald-100 text-emerald-700',
  inactive: 'bg-slate-100 text-slate-600',
  error: 'bg-red-100 text-red-700',
  retired: 'bg-zinc-100 text-zinc-600',
  allow: 'bg-emerald-100 text-emerald-700',
  warn: 'bg-amber-100 text-amber-700',
  deny: 'bg-red-100 text-red-700',
  open: 'bg-red-100 text-red-700',
  ack: 'bg-amber-100 text-amber-700',
  resolved: 'bg-emerald-100 text-emerald-700',
  ignored: 'bg-slate-100 text-slate-600',
  matched: 'bg-emerald-100 text-emerald-700',
  missing: 'bg-amber-100 text-amber-700',
  expired: 'bg-red-100 text-red-700',
  reused: 'bg-red-100 text-red-700',
  mismatch: 'bg-red-100 text-red-700',
  off: 'bg-slate-100 text-slate-600',
  high: 'bg-red-100 text-red-700',
  warnSeverity: 'bg-amber-100 text-amber-700',
};

function short(value: string | null | undefined, size = 10) {
  if (!value) return '—';
  return value.length <= size ? value : `${value.slice(0, size)}...`;
}

function statusClass(value: string) {
  return STATUS_BADGE[value] ?? 'bg-slate-100 text-slate-700';
}

function bridgeToForm(bridge: BridgeDetail): BridgeForm {
  return {
    bridgeCode: bridge.bridgeCode,
    label: bridge.label,
    bridgeType: bridge.bridgeType,
    databaseType: bridge.databaseType,
    datasetType: bridge.datasetType,
    status: bridge.status,
    baseUrl: bridge.baseUrl,
    pathSlug: bridge.pathSlug,
    healthcheckUrl: bridge.healthcheckUrl ?? '',
    sourceKey: bridge.sourceKey ?? '',
    preparedMode: bridge.preparedMode,
    readOnlyMode: bridge.readOnlyMode ? 'true' : 'false',
    allowedHandlersCsv: bridge.allowedHandlers.join(', '),
    allowedIpsCsv: bridge.allowedIps.join(', '),
  };
}

function formToPayload(form: BridgeForm) {
  return {
    bridgeCode: form.bridgeCode.trim(),
    label: form.label.trim(),
    bridgeType: form.bridgeType.trim(),
    databaseType: form.databaseType,
    datasetType: form.datasetType,
    status: form.status,
    baseUrl: form.baseUrl.trim(),
    pathSlug: form.pathSlug.trim(),
    healthcheckUrl: form.healthcheckUrl.trim() || null,
    sourceKey: form.sourceKey.trim() || null,
    preparedMode: form.preparedMode,
    readOnlyMode: form.readOnlyMode === 'true',
    allowedHandlers: form.allowedHandlersCsv.split(',').map((item) => item.trim()).filter(Boolean),
    allowedIps: form.allowedIpsCsv.split(',').map((item) => item.trim()).filter(Boolean),
  };
}

export default function BridgeRegistryPage() {
  const navigate = useNavigate();
  const [bridges, setBridges] = useState<BridgeSummary[]>([]);
  const [selectedBridgeCode, setSelectedBridgeCode] = useState<string | null>(null);
  const [detail, setDetail] = useState<BridgeDetail | null>(null);
  const [form, setForm] = useState<BridgeForm>(EMPTY_FORM);
  const [accessLogs, setAccessLogs] = useState<BridgeAccessLog[]>([]);
  const [alerts, setAlerts] = useState<BridgeAlert[]>([]);
  const [configExport, setConfigExport] = useState('');
  const [preparedRequestId, setPreparedRequestId] = useState('');
  const [prepareHandler, setPrepareHandler] = useState('sql');
  const [prepareAction, setPrepareAction] = useState('');
  const [prepareExpectedIp, setPrepareExpectedIp] = useState('');
  const [prepareExpiresSec, setPrepareExpiresSec] = useState('300');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);

  const loadBridges = useCallback(async (
    preferredBridgeCode?: string | null,
    currentBridgeCode?: string | null,
  ) => {
    setLoading(true);
    try {
      const data = await api.get<{ items: BridgeSummary[]; total: number }>('/tenant-bridges');
      setBridges(data.items ?? []);

      const nextBridgeCode = preferredBridgeCode
        ?? currentBridgeCode
        ?? data.items?.[0]?.bridgeCode
        ?? null;

      setSelectedBridgeCode(nextBridgeCode);
      if (!nextBridgeCode) {
        setDetail(null);
        setForm(EMPTY_FORM);
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to load bridges';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadLogsAndAlerts = useCallback(async (bridgeCode: string) => {
    try {
      const [logs, alertData] = await Promise.all([
        api.get<Paginated<BridgeAccessLog>>(`/tenant-bridges/${encodeURIComponent(bridgeCode)}/access-logs?limit=20&page=1`),
        api.get<Paginated<BridgeAlert>>(`/tenant-bridges/${encodeURIComponent(bridgeCode)}/alerts?limit=20&page=1`),
      ]);
      setAccessLogs(logs.items ?? []);
      setAlerts(alertData.items ?? []);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to load runtime data';
      toast.error(message);
    }
  }, []);

  const loadBridgeDetail = useCallback(async (bridgeCode: string) => {
    try {
      const bridge = await api.get<BridgeDetail>(`/tenant-bridges/${encodeURIComponent(bridgeCode)}`);
      setDetail(bridge);
      setForm(bridgeToForm(bridge));
      await loadLogsAndAlerts(bridgeCode);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to load bridge';
      toast.error(message);
    }
  }, [loadLogsAndAlerts]);

  useEffect(() => {
    void loadBridges(null, null);
  }, [loadBridges]);

  useEffect(() => {
    if (!selectedBridgeCode) {
      setAccessLogs([]);
      setAlerts([]);
      return;
    }
    void loadBridgeDetail(selectedBridgeCode);
  }, [selectedBridgeCode, loadBridgeDetail]);

  const updateForm = <K extends keyof BridgeForm>(key: K, value: BridgeForm[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const runAction = async (key: string, fn: () => Promise<void>) => {
    setBusy(key);
    try {
      await fn();
    } finally {
      setBusy(null);
    }
  };

  const handleNew = () => {
    setSelectedBridgeCode(null);
    setDetail(null);
    setForm(EMPTY_FORM);
    setConfigExport('');
    setPreparedRequestId('');
  };

  const handleSave = async () => {
    const payload = formToPayload(form);
    if (!payload.bridgeCode || !payload.baseUrl) {
      toast.error('bridgeCode and baseUrl are required');
      return;
    }

    await runAction('save', async () => {
      if (detail && detail.bridgeCode === payload.bridgeCode) {
        await api.patch(`/tenant-bridges/${encodeURIComponent(detail.bridgeCode)}`, payload);
        toast.success('Bridge updated');
        await loadBridges(payload.bridgeCode, payload.bridgeCode);
        return;
      }

      const created = await api.post<BridgeSummary>('/tenant-bridges', payload);
      toast.success('Bridge created');
      await loadBridges(created.bridgeCode, created.bridgeCode);
    });
  };

  const handleDelete = async () => {
    if (!detail) return;
    if (!window.confirm(`Delete bridge "${detail.bridgeCode}"?`)) return;

    await runAction('delete', async () => {
      await api.delete(`/tenant-bridges/${encodeURIComponent(detail.bridgeCode)}`);
      toast.success('Bridge deleted');
      setConfigExport('');
      setPreparedRequestId('');
      await loadBridges(null, null);
    });
  };

  const handleTest = async () => {
    if (!detail) return;
    await runAction('test', async () => {
      const result = await api.post<{ reachable: boolean; latencyMs: number; error: string }>(
        `/tenant-bridges/${encodeURIComponent(detail.bridgeCode)}/test`,
        {},
      );
      if (result.reachable) {
        toast.success(`Bridge reachable in ${result.latencyMs}ms`);
      } else {
        toast.error(result.error || 'Bridge did not respond');
      }
      await loadBridgeDetail(detail.bridgeCode);
    });
  };

  const handleExport = async () => {
    if (!detail) return;
    await runAction('export', async () => {
      const exported = await api.post<BridgeConfigExport>(
        `/tenant-bridges/${encodeURIComponent(detail.bridgeCode)}/config/export`,
        {},
      );
      const nextConfig = {
        passkeys: exported.passkeys,
        preparedMode: exported.preparedMode,
        platformValidateUrl: exported.platformValidateUrl,
        platformCompleteUrl: exported.platformCompleteUrl,
      };
      setConfigExport(JSON.stringify(nextConfig, null, 2));
      toast.success('Bridge config prepared');
    });
  };

  const handleGenerateNextKey = async () => {
    if (!detail) return;
    await runAction('generate-key', async () => {
      const result = await api.post<{ key: string; keyMasked: string }>(
        `/tenant-bridges/${encodeURIComponent(detail.bridgeCode)}/keys/generate`,
        { keySlot: 'next' },
      );
      toast.success(`Next key generated: ${result.keyMasked}`);
      setConfigExport(`Next key\n${result.key}`);
      await loadBridgeDetail(detail.bridgeCode);
    });
  };

  const handleRotate = async () => {
    if (!detail) return;
    await runAction('rotate-key', async () => {
      await api.post(`/tenant-bridges/${encodeURIComponent(detail.bridgeCode)}/keys/rotate`, {});
      toast.success('Bridge key rotated');
      await loadBridgeDetail(detail.bridgeCode);
    });
  };

  const handlePrepare = async () => {
    if (!detail) return;
    await runAction('prepare', async () => {
      const expiresInSec = Number(prepareExpiresSec || '300');
      const result = await api.post<{
        requestId: string;
        headers: Record<string, string>;
      }>(`/tenant-bridges/${encodeURIComponent(detail.bridgeCode)}/prepare`, {
        allowedHandler: prepareHandler.trim() || null,
        allowedAction: prepareAction.trim() || null,
        expectedIp: prepareExpectedIp.trim() || null,
        expiresInSec: Number.isFinite(expiresInSec) ? expiresInSec : 300,
      });
      setPreparedRequestId(result.requestId);
      toast.success('Prepared request created');
      await navigator.clipboard.writeText(result.requestId).catch(() => {});
      await loadLogsAndAlerts(detail.bridgeCode);
    });
  };

  const handleAlertStatus = async (alertId: string, status: string) => {
    if (!detail) return;
    await runAction(`alert-${alertId}`, async () => {
      await api.post(`/tenant-bridges/${encodeURIComponent(detail.bridgeCode)}/alerts/${alertId}/status`, { status });
      toast.success(`Alert ${status}`);
      await loadLogsAndAlerts(detail.bridgeCode);
    });
  };

  const copyText = async (value: string, label: string) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success(`${label} copied`);
    } catch {
      toast.error(`Failed to copy ${label}`);
    }
  };

  const errorText = detail?.lastError || '';

  return (
    <div className="space-y-6 p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold">
            <Network className="h-6 w-6 text-sky-600" />
            Bridge Registry
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Register bridge endpoints, rotate keys, export runtime config, prepare requests, and inspect suspicious access.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => navigate('/admin/data-transfer')}>
            Data Transfer
          </Button>
          <Button variant="outline" size="sm" onClick={handleNew}>
            New Bridge
          </Button>
          <Button variant="outline" size="sm" onClick={() => void loadBridges(selectedBridgeCode)}>
            <RefreshCw className={cn('mr-1 h-4 w-4', loading && 'animate-spin')} />
            Refresh
          </Button>
        </div>
      </div>

      <div className="grid gap-6 xl:grid-cols-[340px_minmax(0,1fr)]">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between text-base">
              Bridges
              <Badge variant="outline">{bridges.length}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {loading ? (
              <div className="flex items-center justify-center py-12 text-sm text-muted-foreground">
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Loading bridges...
              </div>
            ) : bridges.length === 0 ? (
              <div className="rounded-lg border border-dashed p-6 text-sm text-muted-foreground">
                No bridges yet. Create the first test bridge on the right.
              </div>
            ) : (
              bridges.map((bridge) => (
                <button
                  key={bridge.id}
                  type="button"
                  onClick={() => setSelectedBridgeCode(bridge.bridgeCode)}
                  className={cn(
                    'w-full rounded-xl border p-3 text-left transition-colors',
                    selectedBridgeCode === bridge.bridgeCode
                      ? 'border-sky-300 bg-sky-50'
                      : 'hover:bg-muted/40',
                  )}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className="font-medium">{bridge.label}</div>
                      <div className="text-xs text-muted-foreground">{bridge.bridgeCode}</div>
                    </div>
                    <Badge className={cn('text-[10px]', statusClass(bridge.status))}>{bridge.status}</Badge>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-1">
                    <Badge variant="outline" className="text-[10px]">{bridge.databaseType}</Badge>
                    <Badge variant="outline" className="text-[10px]">{bridge.datasetType}</Badge>
                    <Badge variant="outline" className="text-[10px]">{bridge.preparedMode}</Badge>
                  </div>
                  <div className="mt-2 text-[11px] text-muted-foreground">
                    {bridge.bridgeUrl}
                  </div>
                </button>
              ))
            )}
          </CardContent>
        </Card>

        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between text-base">
                {detail ? 'Bridge Detail' : 'Create Bridge'}
                {detail && (
                  <div className="flex items-center gap-2">
                    {detail.lastTestOk === true && <ShieldCheck className="h-4 w-4 text-emerald-600" />}
                    {detail.lastTestOk === false && <ShieldAlert className="h-4 w-4 text-red-600" />}
                    <Badge className={cn('text-[10px]', statusClass(detail.status))}>{detail.status}</Badge>
                  </div>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <Field label="Bridge Code">
                  <Input value={form.bridgeCode} onChange={(e) => updateForm('bridgeCode', e.target.value)} placeholder="cms-main" />
                </Field>
                <Field label="Label">
                  <Input value={form.label} onChange={(e) => updateForm('label', e.target.value)} placeholder="CMS Main Bridge" />
                </Field>
                <Field label="Bridge Type">
                  <Input value={form.bridgeType} onChange={(e) => updateForm('bridgeType', e.target.value)} placeholder="ashx" />
                </Field>
                <Field label="Source Key">
                  <Input value={form.sourceKey} onChange={(e) => updateForm('sourceKey', e.target.value)} placeholder="legacy-main" />
                </Field>
                <Field label="Database Type">
                  <Select value={form.databaseType} onValueChange={(value) => updateForm('databaseType', value as DatabaseType)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="MSSQL">MSSQL</SelectItem>
                      <SelectItem value="FIREBIRD">FIREBIRD</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Dataset Type">
                  <Select value={form.datasetType} onValueChange={(value) => updateForm('datasetType', value as DatasetType)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="CARGOERP">CARGOERP</SelectItem>
                      <SelectItem value="EZSHIP">EZSHIP</SelectItem>
                      <SelectItem value="PRO2">PRO2</SelectItem>
                      <SelectItem value="CUSTOM">CUSTOM</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Status">
                  <Select value={form.status} onValueChange={(value) => updateForm('status', value as BridgeStatus)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">active</SelectItem>
                      <SelectItem value="inactive">inactive</SelectItem>
                      <SelectItem value="error">error</SelectItem>
                      <SelectItem value="retired">retired</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Prepared Mode">
                  <Select value={form.preparedMode} onValueChange={(value) => updateForm('preparedMode', value as PreparedMode)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="off">off</SelectItem>
                      <SelectItem value="monitor">monitor</SelectItem>
                      <SelectItem value="enforce">enforce</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Read Only">
                  <Select value={form.readOnlyMode} onValueChange={(value) => updateForm('readOnlyMode', value as 'true' | 'false')}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="true">true</SelectItem>
                      <SelectItem value="false">false</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Base URL">
                  <Input value={form.baseUrl} onChange={(e) => updateForm('baseUrl', e.target.value)} placeholder="https://erp.example.com" />
                </Field>
                <Field label="Path Slug">
                  <Input value={form.pathSlug} onChange={(e) => updateForm('pathSlug', e.target.value)} placeholder="2db3b40e-bridge" />
                </Field>
                <Field label="Healthcheck URL">
                  <Input value={form.healthcheckUrl} onChange={(e) => updateForm('healthcheckUrl', e.target.value)} placeholder="optional override" />
                </Field>
                <Field label="Allowed Handlers">
                  <Input value={form.allowedHandlersCsv} onChange={(e) => updateForm('allowedHandlersCsv', e.target.value)} placeholder="sql, list, status" />
                </Field>
                <Field label="Allowed IPs" className="md:col-span-2">
                  <Input value={form.allowedIpsCsv} onChange={(e) => updateForm('allowedIpsCsv', e.target.value)} placeholder="203.0.113.12, 198.51.100.8" />
                </Field>
              </div>

              <div className="flex flex-wrap gap-2">
                <Button onClick={() => void handleSave()} disabled={busy === 'save'}>
                  {busy === 'save' ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-1 h-4 w-4" />}
                  {detail ? 'Save Bridge' : 'Create Bridge'}
                </Button>
                <Button variant="outline" onClick={() => void handleTest()} disabled={!detail || busy === 'test'}>
                  {busy === 'test' ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <TestTube2 className="mr-1 h-4 w-4" />}
                  Test
                </Button>
                <Button variant="outline" onClick={() => void handleExport()} disabled={!detail || busy === 'export'}>
                  {busy === 'export' ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Copy className="mr-1 h-4 w-4" />}
                  Export Config
                </Button>
                <Button variant="outline" onClick={() => void handleGenerateNextKey()} disabled={!detail || busy === 'generate-key'}>
                  {busy === 'generate-key' ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <KeyRound className="mr-1 h-4 w-4" />}
                  Generate Next Key
                </Button>
                <Button variant="outline" onClick={() => void handleRotate()} disabled={!detail || busy === 'rotate-key'}>
                  {busy === 'rotate-key' ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-1 h-4 w-4" />}
                  Rotate Key
                </Button>
                <Button variant="destructive" onClick={() => void handleDelete()} disabled={!detail || busy === 'delete'}>
                  {busy === 'delete' ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Trash2 className="mr-1 h-4 w-4" />}
                  Delete
                </Button>
              </div>

              {detail && (
                <div className="grid gap-3 md:grid-cols-3">
                  <StatusCard label="Bridge URL" value={detail.bridgeUrl} />
                  <StatusCard label="Last Tested" value={detail.lastTestedAt ? new Date(detail.lastTestedAt).toLocaleString() : 'Never'} />
                  <StatusCard label="Health" value={detail.lastTestOk === null ? 'Unknown' : detail.lastTestOk ? 'Reachable' : 'Failed'} tone={detail.lastTestOk === false ? 'danger' : detail.lastTestOk ? 'ok' : 'muted'} />
                </div>
              )}

              {errorText && (
                <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                  <div className="mb-1 flex items-center gap-2 font-medium">
                    <AlertTriangle className="h-4 w-4" />
                    Last Error
                  </div>
                  {errorText}
                </div>
              )}
            </CardContent>
          </Card>

          <Tabs defaultValue="runtime">
            <TabsList>
              <TabsTrigger value="runtime">Runtime</TabsTrigger>
              <TabsTrigger value="keys">Keys</TabsTrigger>
              <TabsTrigger value="logs">Access Logs</TabsTrigger>
              <TabsTrigger value="alerts">Alerts</TabsTrigger>
            </TabsList>

            <TabsContent value="runtime">
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Prepared Session + Config Export</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid gap-4 md:grid-cols-2">
                    <Field label="Allowed Handler">
                      <Input value={prepareHandler} onChange={(e) => setPrepareHandler(e.target.value)} placeholder="sql" />
                    </Field>
                    <Field label="Allowed Action">
                      <Input value={prepareAction} onChange={(e) => setPrepareAction(e.target.value)} placeholder="optional action" />
                    </Field>
                    <Field label="Expected IP">
                      <Input value={prepareExpectedIp} onChange={(e) => setPrepareExpectedIp(e.target.value)} placeholder="optional client IP" />
                    </Field>
                    <Field label="Expires (sec)">
                      <Input value={prepareExpiresSec} onChange={(e) => setPrepareExpiresSec(e.target.value)} placeholder="300" />
                    </Field>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button onClick={() => void handlePrepare()} disabled={!detail || busy === 'prepare'}>
                      {busy === 'prepare' ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <ShieldCheck className="mr-1 h-4 w-4" />}
                      Prepare Request
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => preparedRequestId && void copyText(preparedRequestId, 'Prepared request ID')}
                      disabled={!preparedRequestId}
                    >
                      <Copy className="mr-1 h-4 w-4" />
                      Copy Request ID
                    </Button>
                  </div>

                  <Field label="Prepared Request ID">
                    <Input value={preparedRequestId} readOnly placeholder="Generated request ID appears here" />
                  </Field>

                  <Field label="Exported booxapi config fragment">
                    <Textarea value={configExport} onChange={(e) => setConfigExport(e.target.value)} rows={12} placeholder="Click Export Config to generate runtime settings." />
                  </Field>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="keys">
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Key Slots</CardTitle>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Slot</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Fingerprint</TableHead>
                        <TableHead>Last Used</TableHead>
                        <TableHead>Last IP</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {detail?.keys?.length ? detail.keys.map((key) => (
                        <TableRow key={key.id}>
                          <TableCell className="font-medium">{key.keySlot}</TableCell>
                          <TableCell><Badge className={cn('text-[10px]', statusClass(key.status))}>{key.status}</Badge></TableCell>
                          <TableCell className="font-mono text-xs">{short(key.keyFingerprint, 18)}</TableCell>
                          <TableCell>{key.lastUsedAt ? new Date(key.lastUsedAt).toLocaleString() : '—'}</TableCell>
                          <TableCell>{key.lastUsedIp ?? '—'}</TableCell>
                        </TableRow>
                      )) : (
                        <TableRow>
                          <TableCell colSpan={5} className="text-muted-foreground">No keys yet.</TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="logs">
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Recent Access Logs</CardTitle>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>When</TableHead>
                        <TableHead>Handler</TableHead>
                        <TableHead>Prepared</TableHead>
                        <TableHead>Decision</TableHead>
                        <TableHead>IP</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Reason</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {accessLogs.length ? accessLogs.map((log) => (
                        <TableRow key={log.id}>
                          <TableCell className="text-xs">{new Date(log.created_at).toLocaleString()}</TableCell>
                          <TableCell>
                            <div className="font-medium">{log.handler ?? '—'}</div>
                            <div className="text-xs text-muted-foreground">{log.action ?? log.event_type}</div>
                          </TableCell>
                          <TableCell><Badge className={cn('text-[10px]', statusClass(log.prepared_status))}>{log.prepared_status}</Badge></TableCell>
                          <TableCell><Badge className={cn('text-[10px]', statusClass(log.decision))}>{log.decision}</Badge></TableCell>
                          <TableCell>{log.source_ip ?? '—'}</TableCell>
                          <TableCell>{log.status_code ?? '—'}</TableCell>
                          <TableCell className="text-xs text-muted-foreground">{log.reason ?? '—'}</TableCell>
                        </TableRow>
                      )) : (
                        <TableRow>
                          <TableCell colSpan={7} className="text-muted-foreground">No runtime logs yet.</TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="alerts">
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Runtime Alerts</CardTitle>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>When</TableHead>
                        <TableHead>Code</TableHead>
                        <TableHead>Severity</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Detail</TableHead>
                        <TableHead>Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {alerts.length ? alerts.map((alert) => (
                        <TableRow key={alert.id}>
                          <TableCell className="text-xs">{new Date(alert.created_at).toLocaleString()}</TableCell>
                          <TableCell>
                            <div className="font-medium">{alert.alert_code}</div>
                            <div className="text-xs text-muted-foreground">{alert.title}</div>
                          </TableCell>
                          <TableCell><Badge className={cn('text-[10px]', statusClass(alert.severity === 'warn' ? 'warnSeverity' : alert.severity))}>{alert.severity}</Badge></TableCell>
                          <TableCell><Badge className={cn('text-[10px]', statusClass(alert.status))}>{alert.status}</Badge></TableCell>
                          <TableCell className="text-xs text-muted-foreground">{alert.detail}</TableCell>
                          <TableCell>
                            <div className="flex gap-2">
                              <Button variant="outline" size="sm" onClick={() => void handleAlertStatus(alert.id, 'ack')}>
                                Ack
                              </Button>
                              <Button variant="outline" size="sm" onClick={() => void handleAlertStatus(alert.id, 'resolved')}>
                                Resolve
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      )) : (
                        <TableRow>
                          <TableCell colSpan={6} className="text-muted-foreground">No alerts.</TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}

function Field({
  label,
  className,
  children,
}: {
  label: string;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div className={cn('space-y-2', className)}>
      <Label>{label}</Label>
      {children}
    </div>
  );
}

function StatusCard({
  label,
  value,
  tone = 'muted',
}: {
  label: string;
  value: string;
  tone?: 'muted' | 'ok' | 'danger';
}) {
  return (
    <div className={cn(
      'rounded-xl border p-3',
      tone === 'ok' && 'border-emerald-200 bg-emerald-50',
      tone === 'danger' && 'border-red-200 bg-red-50',
    )}
    >
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="mt-1 break-all text-sm font-medium">{value}</div>
    </div>
  );
}
